import { removeAbbr } from '@/utils/time'
import round from 'lodash-es/round'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { buildMarkLine } from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import XAxis = echarts.EChartOption.XAxis
import YAxis = echarts.EChartOption.YAxis
import SeriesLine = echarts.EChartOption.SeriesLine

export function buildSummaryReportLineChartOption(options: {
  actualDataPoints: DataPoint[]
  rollingAvgDataPoints?: DataPoint[]
  timezone: string
  startTimestamp: number
  endTimestamp: number
  lineColor: string
  unitLabel: string
  minY: number
  maxY?: number
  markLineNumbers?: number[]
  yAxisOption?: object
}): any {
  const {
    actualDataPoints,
    rollingAvgDataPoints = [],
    timezone,
    startTimestamp,
    endTimestamp,
    lineColor,
    unitLabel,
    minY,
    maxY,
    markLineNumbers = [],
    yAxisOption = {},
  } = options
  const minX = moment(startTimestamp).toDate()
  const maxX = moment(endTimestamp).toDate()
  const series: SeriesLine[] | any[] = [
    {
      type: 'line',
      silent: true,
      markLine: buildMarkLine(markLineNumbers),
    },
  ]

  const actualDataSeries = actualDataPoints.map((dp, index) => ({
    value: [moment(dp.timestamp).toDate(), dp.value, dp],
  }))
  const rollingAvgDataSeries = rollingAvgDataPoints.map((dp, index) => ({
    value: [moment(dp.timestamp).toDate(), dp.value, dp],
  }))

  if (actualDataSeries.length) {
    series.push({
      name: 'actualData',
      type: 'line',
      label: { show: false },
      lineStyle: {
        width: 2,
        color: lineColor,
        opacity: 0.5,
      },
      showSymbol: false,
      itemStyle: {
        borderColor: lineColor,
      },
      smooth: true,
      smoothMonotone: 'x',
      data: actualDataSeries,
    })
  }

  if (rollingAvgDataSeries.length) {
    series.push({
      type: 'line',
      label: { show: false },
      lineStyle: {
        width: 4,
        color: lineColor,
        opacity: 0.8,
      },
      showSymbol: false,
      symbol:
        'image://data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
      silent: true,
      smooth: true,
      smoothMonotone: 'x',
      data: rollingAvgDataSeries,
    })
  }

  return {
    xAxis: {
      type: 'time',
      splitLine: {
        show: false,
      },
      interval: maxX.getTime(),
      axisLabel: {
        show: true,
        showMinLabel: true,
        showMaxLabel: true,
        formatter: param => {
          if (param == null) return
          return moment(param)
            .tz(timezone)
            .format(CHART_DATE_FORMAT.DATE)
        },
      },
      min: minX,
      max: maxX,
    } as XAxis | any,
    yAxis: {
      type: 'value',
      min: minY,
      max: maxY,
      axisLabel: { show: false },
      axisLine: { show: false },
      splitLine: { show: false },
      ...yAxisOption,
    } as YAxis,
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        if (params[0].seriesName !== 'actualData') {
          return
        }
        const date = moment(params[0].value[2].timestamp)
          .tz(timezone)
          .format(removeAbbr(timezone, CHART_DATE_FORMAT.TIME_ABBR))
        const value = round(params[0].value[2].value, 1)
        return `${date} ${value}${unitLabel}`
      },
    },
    series,
  }
}
